<template>
  <v-container fluid class="pa-4">
    <!-- ✅ HEADER PRINCIPAL -->
    <div class="page-header mb-4">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="deep-purple-darken-1" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-account-group</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Gerenciar Grupos</h1>
              <div class="text-caption text-grey">
                Organize usuários em grupos e configure conjuntos de permissões
              </div>
            </div>
          </div>
          
          <v-spacer></v-spacer>
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltarMenu"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
            <v-btn
              color="deep-purple-darken-1"
              variant="flat"
              @click="carregarGrupos"
              class="text-none mr-2"
              prepend-icon="mdi-refresh"
              :loading="carregando"
            >
              Atualizar
            </v-btn>

            <v-btn
              color="primary"
              variant="flat"
              @click="abrirDialogNovo"
              class="text-none"
              prepend-icon="mdi-plus"
            >
              Novo Grupo
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <v-card class="mb-4">
      <v-card-title class="d-flex align-center pa-4 bg-grey-lighten-4">
        <v-icon left>mdi-table</v-icon>
        <span class="text-h6 ml-2">Lista de Grupos</span>
      </v-card-title>

      <v-card-text>
        <!-- Campo de Busca -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="busca"
              prepend-inner-icon="mdi-magnify"
              label="Buscar grupo"
              placeholder="Digite o nome do grupo..."
              variant="outlined"
              density="compact"
              clearable
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Tabela de Grupos -->
        <v-data-table
          :headers="headers"
          :items="grupos"
          :search="busca"
          :loading="carregando"
          class="elevation-1"
          :items-per-page="10"
        >
          <!-- Coluna de Usuários -->
          <template #[`item.users`]="{ item }">
            <v-chip color="blue lighten-3" small>
              {{ item.users ? item.users.length : 0 }} usuários
            </v-chip>
          </template>

          <!-- Coluna de Apps -->
          <template #[`item.apps`]="{ item }">
            <v-chip color="green lighten-3" small>
              {{ item.apps ? item.apps.length : 0 }} aplicações
            </v-chip>
          </template>

          <!-- Ações -->
          <template #[`item.actions`]="{ item }">
            <v-btn icon small @click="gerenciarPermissoes(item)" color="info" title="Gerenciar Permissões">
              <v-icon>mdi-shield-lock</v-icon>
            </v-btn>
            <v-btn icon small @click="editarGrupo(item)" color="primary" title="Editar">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn 
              icon 
              small 
              @click="confirmarExclusao(item)" 
              color="error" 
              :disabled="item.description.toLowerCase() === 'administrador'"
              :title="item.description.toLowerCase() === 'administrador' ? 'Grupo protegido - não pode ser excluído' : 'Excluir'"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dialog de Criar/Editar Grupo -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <!-- Overlay de Loading -->
        <v-overlay :model-value="salvando" contained persistent class="align-center justify-center">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              size="64"
              color="primary"
              class="mb-4"
            ></v-progress-circular>
            <div class="text-h6 text-white">{{ mensagemSalvamento }}</div>
          </div>
        </v-overlay>

        <v-card-title>
          <span class="text-h5">{{ modoEdicao ? 'Editar Grupo' : 'Novo Grupo' }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="form" v-model="formValido">
            <v-text-field
              v-model="grupoAtual.description"
              label="Descrição do Grupo"
              :rules="[v => !!v || 'Descrição é obrigatória']"
              required
            ></v-text-field>

            <!-- Opção de copiar permissões (apenas ao criar) -->
            <div v-if="!modoEdicao" class="mt-4">
              <v-divider class="mb-4"></v-divider>
              
              <v-checkbox
                v-model="copiarPermissoes"
                label="Copiar permissões de outro grupo"
                color="primary"
                hide-details
              ></v-checkbox>

              <v-select
                v-if="copiarPermissoes"
                v-model="grupoParaCopiar"
                :items="gruposDisponiveis"
                item-title="description"
                item-value="group_id"
                label="Selecione o grupo base"
                prepend-icon="mdi-content-copy"
                hint="As permissões deste grupo serão copiadas para o novo grupo"
                persistent-hint
                class="mt-3"
              ></v-select>

              <!-- Botão para Editar Permissões -->
              <v-btn
                v-if="copiarPermissoes && grupoParaCopiar && resumoPermissoes"
                variant="tonal"
                color="primary"
                class="mt-3"
                block
                @click="editarPermissoesAntesCopiar"
              >
                <v-icon start>mdi-pencil</v-icon>
                Editar Permissões Antes de Copiar
              </v-btn>

              <v-progress-circular
                v-if="copiarPermissoes && grupoParaCopiar && carregandoResumo"
                indeterminate
                color="primary"
                size="24"
                class="mt-3"
              ></v-progress-circular>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="fecharDialog">
            Cancelar
          </v-btn>
          <v-btn color="primary" @click="salvarGrupo" :loading="salvando" :disabled="!formValido">
            <v-icon left>mdi-content-save</v-icon>
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Edição de Permissões -->
    <v-dialog v-model="dialogEditarPermissoes" max-width="1200px" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center pa-4 bg-primary">
          <v-icon color="white" class="mr-2">mdi-pencil</v-icon>
          <span class="text-h6 text-white">Editar Permissões Antes de Copiar</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="dialogEditarPermissoes = false" color="white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-4">
            <v-icon start>mdi-information</v-icon>
            Ajuste as permissões que deseja. Apenas as marcadas serão copiadas para o novo grupo.
          </v-alert>

          <!-- Filtros -->
          <v-row class="mb-3">
            <!-- Busca -->
            <v-col cols="12" md="5">
              <v-text-field
                v-model="buscaPermissoes"
                prepend-inner-icon="mdi-magnify"
                label="Buscar"
                placeholder="Busque por descrição ou app_name..."
                hint="Busca por descrição, app_name, tipo ou pai"
                persistent-hint
                density="compact"
                variant="outlined"
                clearable
              ></v-text-field>
            </v-col>

            <!-- Tipo -->
            <v-col cols="12" md="2">
              <v-select
                v-model="filtroTipoEdicao"
                :items="tiposDisponiveisEdicao"
                label="Tipo"
                prepend-inner-icon="mdi-filter"
                hint="menu, api..."
                persistent-hint
                density="compact"
                variant="outlined"
                clearable
              ></v-select>
            </v-col>

            <!-- Pai -->
            <v-col cols="12" md="3">
              <v-select
                v-model="filtroPaiEdicao"
                :items="paisDisponiveisEdicao"
                item-title="label"
                item-value="value"
                label="Categoria"
                prepend-inner-icon="mdi-folder"
                hint="Filtre por módulo"
                persistent-hint
                density="compact"
                variant="outlined"
                clearable
              ></v-select>
            </v-col>

            <!-- Botões -->
            <v-col cols="12" md="2" class="d-flex flex-column gap-1">
              <v-btn size="small" color="success" variant="tonal" @click="marcarTodasEdicao" block>
                <v-icon start size="small">mdi-checkbox-marked</v-icon>
                Marcar
              </v-btn>
              <v-btn size="small" color="error" variant="tonal" @click="desmarcarTodasEdicao" block>
                <v-icon start size="small">mdi-checkbox-blank-outline</v-icon>
                Desmarcar
              </v-btn>
            </v-col>
          </v-row>

          <!-- Tabela de Permissões Editável -->
          <v-data-table
            :headers="headersEdicao"
            :items="permissoesFiltradas"
            :search="buscaPermissoes"
            density="compact"
            :items-per-page="10"
            class="elevation-1"
          >
            <template #[`item.app_name`]="{ item }">
              <v-tooltip location="bottom">
                <template #activator="{ props }">
                  <div v-bind="props">
                    <div v-if="item.description" class="text-body-2">
                      {{ item.description }}
                    </div>
                    <div v-else class="font-weight-bold font-monospace text-body-2 text-grey-darken-2">
                      {{ item.app_name }}
                    </div>
                  </div>
                </template>
                <div class="text-caption">
                  <strong>App Name:</strong> {{ item.app_name }}<br>
                  <span v-if="item.app_type"><strong>Tipo:</strong> {{ item.app_type }}<br></span>
                  <span v-if="item.parent_app"><strong>Pai:</strong> {{ item.parent_app }}</span>
                </div>
              </v-tooltip>
            </template>

            <template #[`item.priv_access`]="{ item }">
              <v-checkbox 
                v-model="item.priv_access" 
                true-value="Y" 
                false-value="N" 
                hide-details 
                density="compact"
                @change="onAccessChange(item)"
              ></v-checkbox>
            </template>

            <template #[`item.priv_insert`]="{ item }">
              <v-checkbox 
                v-model="item.priv_insert" 
                true-value="Y" 
                false-value="N" 
                hide-details 
                density="compact"
                @change="onPermissaoChange(item, 'priv_insert')"
              ></v-checkbox>
            </template>

            <template #[`item.priv_update`]="{ item }">
              <v-checkbox 
                v-model="item.priv_update" 
                true-value="Y" 
                false-value="N" 
                hide-details 
                density="compact"
                @change="onPermissaoChange(item, 'priv_update')"
              ></v-checkbox>
            </template>

            <template #[`item.priv_delete`]="{ item }">
              <v-checkbox 
                v-model="item.priv_delete" 
                true-value="Y" 
                false-value="N" 
                hide-details 
                density="compact"
                @change="onPermissaoChange(item, 'priv_delete')"
              ></v-checkbox>
            </template>

            <template #[`item.priv_export`]="{ item }">
              <v-checkbox 
                v-model="item.priv_export" 
                true-value="Y" 
                false-value="N" 
                hide-details 
                density="compact"
                @change="onPermissaoChange(item, 'priv_export')"
              ></v-checkbox>
            </template>

            <template #[`item.priv_print`]="{ item }">
              <v-checkbox 
                v-model="item.priv_print" 
                true-value="Y" 
                false-value="N" 
                hide-details 
                density="compact"
                @change="onPermissaoChange(item, 'priv_print')"
              ></v-checkbox>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="dialogEditarPermissoes = false">
            Cancelar
          </v-btn>
          <v-btn color="primary" variant="flat" @click="confirmarEdicao">
            <v-icon start>mdi-check</v-icon>
            Confirmar e Usar Estas Permissões
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog de Confirmação de Exclusão -->
    <v-dialog v-model="dialogExclusao" max-width="500px">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza que deseja excluir o grupo <strong>{{ grupoParaExcluir?.description }}</strong>?
          <br><br>
          <v-alert type="warning" dense text>
            Todos os usuários perderão as permissões deste grupo!
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogExclusao = false">
            Cancelar
          </v-btn>
          <v-btn color="error" @click="excluirGrupo" :loading="excluindo">
            <v-icon left>mdi-delete</v-icon>
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import permissionsService from '@/services/permissionsService';
import { useToast } from 'vue-toastification';

export default {
  name: 'GerenciarGrupos',

  data() {
    return {
      grupos: [],
      busca: '',
      carregando: false,
      salvando: false,
      excluindo: false,
      mensagemSalvamento: 'Salvando...',
      dialog: false,
      dialogExclusao: false,
      formValido: false,
      modoEdicao: false,
      grupoAtual: this.grupoVazio(),
      grupoParaExcluir: null,
      copiarPermissoes: false,
      grupoParaCopiar: null,
      resumoPermissoes: null,
      carregandoResumo: false,
      dialogEditarPermissoes: false,
      permissoesEditaveis: [],
      buscaPermissoes: '',
      filtroTipoEdicao: null,
      filtroPaiEdicao: null,
      permissoesCustomizadas: false,
      headers: [
        { text: 'ID', value: 'group_id', sortable: true },
        { text: 'Descrição', value: 'description', sortable: true },
        { text: 'Usuários', value: 'users', sortable: false, align: 'center' },
        { text: 'Aplicações', value: 'apps', sortable: false, align: 'center' },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center' }
      ],
      headersEdicao: [
        { title: 'Descrição / Aplicação', key: 'app_name', sortable: true, width: '500px' },
        { title: 'Acesso', key: 'priv_access', sortable: false, align: 'center', width: '70px' },
        { title: 'Inserir', key: 'priv_insert', sortable: false, align: 'center', width: '70px' },
        { title: 'Editar', key: 'priv_update', sortable: false, align: 'center', width: '70px' },
        { title: 'Deletar', key: 'priv_delete', sortable: false, align: 'center', width: '70px' },
        { title: 'Exportar', key: 'priv_export', sortable: false, align: 'center', width: '80px' },
        { title: 'Imprimir', key: 'priv_print', sortable: false, align: 'center', width: '80px' }
      ]
    };
  },

  computed: {
    toast() {
      return useToast();
    },

    tiposDisponiveisEdicao() {
      const tipos = [...new Set(this.permissoesEditaveis.map(p => p.app_type).filter(t => t))];
      return tipos.sort();
    },

    paisDisponiveisEdicao() {
      const categoriasInfo = {
        'menu_producao_geral': { label: '🏭 Produção', ordem: 1 },
        'menu_planejamento_geral': { label: '📅 Planejamento', ordem: 2 },
        'menu_maquinas_geral': { label: '⚙️ Máquinas & Equipamentos', ordem: 3 },
        'menu_expediente_geral': { label: '🕐 Expediente & RH', ordem: 4 },
        'menu_suporte_geral': { label: '🎧 Suporte & Comunicação', ordem: 5 },
        'menu_administracao_geral': { label: '🛡️ Administração', ordem: 6 },
        'menu_usuario_geral': { label: '👥 Usuários', ordem: 7 }
      };

      const pais = [...new Set(this.permissoesEditaveis.map(p => p.parent_app).filter(p => p))];
      
      const resultado = pais.map(pai => ({
        value: pai,
        label: categoriasInfo[pai]?.label || pai,
        ordem: categoriasInfo[pai]?.ordem || 999
      })).sort((a, b) => a.ordem - b.ordem);

      return [
        { value: '__sem_pai__', label: '📱 Outras Aplicações', ordem: 1000 },
        ...resultado
      ];
    },

    permissoesFiltradas() {
      let filtradas = this.permissoesEditaveis;

      // Filtrar por tipo
      if (this.filtroTipoEdicao) {
        filtradas = filtradas.filter(p => p.app_type === this.filtroTipoEdicao);
      }

      // Filtrar por pai
      if (this.filtroPaiEdicao) {
        if (this.filtroPaiEdicao === '__sem_pai__') {
          filtradas = filtradas.filter(p => !p.parent_app);
        } else {
          filtradas = filtradas.filter(p => p.parent_app === this.filtroPaiEdicao);
        }
      }

      return filtradas;
    },

    gruposDisponiveis() {
      return this.grupos.filter(g => !this.modoEdicao || g.group_id !== this.grupoAtual.group_id);
    }
  },

  watch: {
    grupoParaCopiar(novoGrupoId) {
      if (novoGrupoId) {
        this.carregarResumoPermissoes(novoGrupoId);
      } else {
        this.resumoPermissoes = null;
      }
    }
  },

  mounted() {
    this.carregarGrupos();
  },

  methods: {
    voltarMenu() {
      this.$router.go(-1); // Volta para a página anterior
    },
    grupoVazio() {
      return {
        description: ''
      };
    },

    async carregarGrupos() {
      this.carregando = true;
      try {
        const response = await permissionsService.listarGrupos();
        if (response.status === 'sucesso') {
          this.grupos = response.grupos;
        }
      } catch (error) {
        console.error('Erro ao carregar grupos:', error);
        this.toast.error('Erro ao carregar grupos');
      } finally {
        this.carregando = false;
      }
    },

    abrirDialogNovo() {
      this.modoEdicao = false;
      this.grupoAtual = this.grupoVazio();
      this.copiarPermissoes = false;
      this.grupoParaCopiar = null;
      this.permissoesCustomizadas = false;
      this.permissoesEditaveis = [];
      this.dialog = true;
    },

    editarGrupo(grupo) {
      this.modoEdicao = true;
      this.grupoAtual = {
        group_id: grupo.group_id,
        description: grupo.description
      };
      this.dialog = true;
    },

    async salvarGrupo() {
      if (!this.$refs.form.validate()) return;

      this.salvando = true;
      try {
        let response;
        
        if (this.modoEdicao) {
          this.mensagemSalvamento = 'Atualizando grupo...';
          response = await permissionsService.atualizarGrupo(this.grupoAtual.group_id, {
            description: this.grupoAtual.description
          });
        } else {
          // Criar novo grupo
          this.mensagemSalvamento = 'Criando grupo...';
          response = await permissionsService.criarGrupo({
            description: this.grupoAtual.description
          });

          // Se criou com sucesso e deve copiar permissões
          if (response.status === 'sucesso' && this.copiarPermissoes) {
            const novoGrupoId = response.grupo.group_id;
            
            // Se permissões foram customizadas, aplicar EM LOTE (MUITO MAIS RÁPIDO!)
            if (this.permissoesCustomizadas && this.permissoesEditaveis.length > 0) {
              const total = this.permissoesEditaveis.length;
              this.mensagemSalvamento = `Aplicando ${total} permissões personalizadas em lote...`;
              
              // Limpar e validar dados antes de enviar
              const permissoesLimpas = this.permissoesEditaveis.map(p => ({
                app_name: p.app_name,
                priv_access: p.priv_access || 'N',
                priv_insert: p.priv_insert || 'N',
                priv_delete: p.priv_delete || 'N',
                priv_update: p.priv_update || 'N',
                priv_export: p.priv_export || 'N',
                priv_print: p.priv_print || 'N'
              })).filter(p => p.app_name); // Remover se não tiver app_name
              
              console.log('📤 Enviando permissões em lote:', {
                grupo_id: novoGrupoId,
                total_original: total,
                total_limpo: permissoesLimpas.length,
                amostra_5_primeiras: permissoesLimpas.slice(0, 5)
              });
              
              if (permissoesLimpas.length === 0) {
                this.toast.error('❌ Nenhuma permissão válida para aplicar');
                return;
              }
              
              try {
                // Enviar TODAS as permissões de uma vez!
                const response = await permissionsService.atualizarPermissoesEmLote(novoGrupoId, permissoesLimpas);
                
                console.log('✅ Resposta do backend:', response);
                
                if (response.status === 'sucesso') {
                  this.toast.success(`✅ ${response.mensagem}`);
                } else {
                  this.toast.error('❌ Erro ao aplicar permissões');
                }
              } catch (err) {
                console.error('❌ Erro ao aplicar permissões em lote:', {
                  erro: err.message,
                  response: err.response?.data,
                  status: err.response?.status
                });
                
                // Mostrar mensagem específica do erro
                const mensagemErro = err.response?.data?.mensagem || 
                                    err.response?.data?.message || 
                                    'Erro ao aplicar permissões';
                this.toast.error(`❌ ${mensagemErro}`);
              }
            } 
            // Senão, copiar do grupo original (MUITO MAIS RÁPIDO!)
            else if (this.grupoParaCopiar) {
              this.mensagemSalvamento = 'Copiando permissões em lote...';
              
              const responseCopia = await permissionsService.copiarPermissoes(novoGrupoId, this.grupoParaCopiar);
              
              if (responseCopia.status === 'sucesso') {
                this.toast.success(`✅ ${responseCopia.mensagem}`);
              } else {
                this.toast.error('❌ Erro ao copiar permissões');
              }
            }
          }
        }

        if (response.status === 'sucesso') {
          if (!this.copiarPermissoes) {
            this.toast.success(response.mensagem);
          }
          this.fecharDialog();
          this.carregarGrupos();
        } else {
          this.toast.error(response.mensagem || 'Erro ao salvar grupo');
        }
      } catch (error) {
        console.error('Erro ao salvar grupo:', error);
        this.toast.error('Erro ao salvar grupo');
      } finally {
        this.salvando = false;
      }
    },

    confirmarExclusao(grupo) {
      this.grupoParaExcluir = grupo;
      this.dialogExclusao = true;
    },

    async excluirGrupo() {
      if (!this.grupoParaExcluir) return;

      this.excluindo = true;
      try {
        const response = await permissionsService.deletarGrupo(this.grupoParaExcluir.group_id);
        
        if (response.status === 'sucesso') {
          this.toast.success(response.mensagem);
          this.dialogExclusao = false;
          this.grupoParaExcluir = null;
          this.carregarGrupos();
        } else {
          this.toast.error(response.mensagem || 'Erro ao excluir grupo');
        }
      } catch (error) {
        console.error('Erro ao excluir grupo:', error);
        this.toast.error('Erro ao excluir grupo');
      } finally {
        this.excluindo = false;
      }
    },

    gerenciarPermissoes(grupo) {
      // Redirecionar para página de permissões
      this.$router.push({
        name: 'GerenciarPermissoes',
        params: { groupId: grupo.group_id }
      });
    },

    async carregarResumoPermissoes(groupId) {
      this.carregandoResumo = true;
      try {
        const response = await permissionsService.listarPermissoesGrupo(groupId);
        
        if (response.status === 'sucesso') {
          const permissoes = response.permissoes;
          
          // Calcular resumo
          this.resumoPermissoes = {
            total: permissoes.length,
            comAcesso: permissoes.filter(p => p.priv_access === 'Y').length,
            comInsert: permissoes.filter(p => p.priv_insert === 'Y').length,
            comUpdate: permissoes.filter(p => p.priv_update === 'Y').length,
            comDelete: permissoes.filter(p => p.priv_delete === 'Y').length,
            comExport: permissoes.filter(p => p.priv_export === 'Y').length,
            comPrint: permissoes.filter(p => p.priv_print === 'Y').length,
            permissoes: permissoes // Guardar para mostrar detalhes
          };
        }
      } catch (error) {
        console.error('Erro ao carregar resumo:', error);
        this.resumoPermissoes = null;
      } finally {
        this.carregandoResumo = false;
      }
    },

    verDetalhesPermissoes() {
      if (!this.resumoPermissoes || !this.resumoPermissoes.permissoes) return;
      
      const permissoesComAcesso = this.resumoPermissoes.permissoes
        .filter(p => p.priv_access === 'Y')
        .map(p => p.app_name)
        .join('\n');
      
      alert(`Aplicações com ACESSO (${this.resumoPermissoes.comAcesso}):\n\n${permissoesComAcesso || 'Nenhuma'}`);
    },

    editarPermissoesAntesCopiar() {
      if (!this.resumoPermissoes || !this.resumoPermissoes.permissoes) return;
      
      // Fazer cópia profunda das permissões para edição
      this.permissoesEditaveis = JSON.parse(JSON.stringify(this.resumoPermissoes.permissoes));
      this.dialogEditarPermissoes = true;
    },

    // Quando marca/desmarca ACESSO, marca/desmarca TODAS as outras permissões
    onAccessChange(item) {
      if (item.priv_access === 'N') {
        // Desmarcou: remove tudo
        item.priv_insert = 'N';
        item.priv_update = 'N';
        item.priv_delete = 'N';
        item.priv_export = 'N';
        item.priv_print = 'N';
      } else {
        // Marcou: marca tudo
        item.priv_insert = 'Y';
        item.priv_update = 'Y';
        item.priv_delete = 'Y';
        item.priv_export = 'Y';
        item.priv_print = 'Y';
      }
    },

    // Quando marca qualquer permissão, marca ACESSO automaticamente
    onPermissaoChange(item, campo) {
      if (item[campo] === 'Y' && item.priv_access === 'N') {
        item.priv_access = 'Y';
      }
    },

    marcarTodasEdicao() {
      this.permissoesEditaveis.forEach(p => {
        p.priv_access = 'Y';
        p.priv_insert = 'Y';
        p.priv_update = 'Y';
        p.priv_delete = 'Y';
        p.priv_export = 'Y';
        p.priv_print = 'Y';
      });
      this.toast.success('Todas as permissões marcadas!');
    },

    desmarcarTodasEdicao() {
      this.permissoesEditaveis.forEach(p => {
        p.priv_access = 'N';
        p.priv_insert = 'N';
        p.priv_update = 'N';
        p.priv_delete = 'N';
        p.priv_export = 'N';
        p.priv_print = 'N';
      });
      this.toast.success('Todas as permissões desmarcadas!');
    },

    confirmarEdicao() {
      // Marcar que as permissões foram customizadas
      this.permissoesCustomizadas = true;
      
      // Atualizar resumo com as permissões editadas
      this.resumoPermissoes = {
        total: this.permissoesEditaveis.length,
        comAcesso: this.permissoesEditaveis.filter(p => p.priv_access === 'Y').length,
        comInsert: this.permissoesEditaveis.filter(p => p.priv_insert === 'Y').length,
        comUpdate: this.permissoesEditaveis.filter(p => p.priv_update === 'Y').length,
        comDelete: this.permissoesEditaveis.filter(p => p.priv_delete === 'Y').length,
        comExport: this.permissoesEditaveis.filter(p => p.priv_export === 'Y').length,
        comPrint: this.permissoesEditaveis.filter(p => p.priv_print === 'Y').length,
        permissoes: this.permissoesEditaveis
      };
      
      this.dialogEditarPermissoes = false;
      this.toast.success('Permissões personalizadas! Elas serão usadas ao criar o grupo.');
    },

    fecharDialog() {
      this.dialog = false;
      this.grupoAtual = this.grupoVazio();
      this.copiarPermissoes = false;
      this.grupoParaCopiar = null;
      this.resumoPermissoes = null;
      this.permissoesCustomizadas = false;
      this.permissoesEditaveis = [];
      this.mensagemSalvamento = 'Salvando...';
      this.$refs.form.resetValidation();
    }
  }
};
</script>

<style scoped>
/* Destacar linha ao passar o mouse */
.v-data-table >>> tbody tr:hover {
  background-color: #e3f2fd !important;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.v-data-table >>> tbody tr:hover td {
  font-weight: 500;
}
</style>
